/**
 * ShareFunnelPanel — Tweet → visit → signup funnel for the Wins-of-the-Week
 * podium cards this user tweets.
 *
 * Renders only when there's data to show (at least one bot view or signup),
 * otherwise stays out of the way to keep the Referrals page clean.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, MousePointerClick, UserPlus, Sparkles } from "lucide-react";

function fmtTimeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const RANK_MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };

export default function ShareFunnelPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/share-funnel")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  const hasSignal = (data.bot_views || 0) + (data.human_views || 0) + (data.signups || 0) > 0;
  if (!hasSignal) return null;

  return (
    <div className="card p-5 mb-5" data-testid="share-funnel-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-500" /> Podium card funnel
          </div>
          <h3 className="font-display font-bold text-lg mt-1 leading-tight">
            Card shares → visits → signups
          </h3>
          <p className="text-xs text-[var(--ink-3)] mt-1">
            Every time someone tweets your <a href="/wins-of-the-week" className="text-emerald-600 font-semibold hover:underline">Wins-of-the-Week</a> card and a visitor clicks through, we track it here.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        <FunnelStat
          icon={Eye} label="Tweet impressions"
          value={data.bot_views} testid="funnel-bot-views"
          subtitle="OG card fetches (proxy)"
        />
        <FunnelStat
          icon={MousePointerClick} label="Card visits"
          value={data.human_views} testid="funnel-human-views"
          subtitle="Real humans landed"
        />
        <FunnelStat
          icon={UserPlus} label="Signups"
          value={data.signups} testid="funnel-signups"
          accent
          subtitle={data.human_views > 0 ? `${data.conversion_rate}% of visits` : "Tweet → free month"}
        />
      </div>

      {data.recent?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)] font-bold mb-2">
            Recent activity
          </div>
          <div className="space-y-1">
            {data.recent.map((row, i) => (
              <div
                key={`${row.created_at}-${i}`}
                className="flex items-center justify-between text-xs gap-2"
                data-testid="funnel-recent-row"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span aria-hidden className="text-base">{RANK_MEDAL[row.rank] || "🏆"}</span>
                  <span className="text-[var(--ink-2)] truncate">
                    {row.is_bot ? "Card unfurled" : "Card visit"}
                    {row.week_ending ? ` · week of ${row.week_ending}` : ""}
                  </span>
                </div>
                <span className="text-[var(--ink-3)] tabular-nums whitespace-nowrap">
                  {fmtTimeAgo(row.created_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FunnelStat({ icon: Icon, label, value, subtitle, accent, testid }) {
  return (
    <div
      className={`rounded-lg p-3 border ${
        accent ? "border-emerald-300 bg-emerald-50" : "border-[var(--line)] bg-[var(--bg-2)]"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-1">
        <Icon size={12} className={accent ? "text-emerald-600" : ""} />
        <span>{label}</span>
      </div>
      <div className={`font-display font-black text-2xl leading-none tabular-nums ${accent ? "text-emerald-700" : ""}`}>
        {value ?? 0}
      </div>
      {subtitle && (
        <div className="text-[10px] text-[var(--ink-3)] mt-1.5 leading-tight">{subtitle}</div>
      )}
    </div>
  );
}
